import { api } from './apiClient';
import { API_ENDPOINTS } from '@/config';

// Types
export type ParticipantRole = 'Candidate' | 'Interviewer';

interface SessionDetails {
  uId: string;
  candidateUId: string;
  interviewerUId: string;
  slotBookingUId: string | null;
  sessionStatus: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime: string | null;
  actualEndTime: string | null;
  acsThreadId: string | null;
  candidateAcsUserId: string;
  interviewerAcsUserId: string | null;
  recordingUrl: string | null;
  durationInMinutes: number | null;
  candidateJoinedAt: string | null;
  interviewerJoinedAt: string | null;
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  error: string | null;
}

interface ValidateAccessResponse {
  hasAccess: boolean;
}

interface GenerateTokenResponse {
  acsUserId: string;
  accessToken: string;
  expiresOn: string;
  sessionDetails: SessionDetails;
}

// Request types
interface GetSessionRequest {
  uId: string;
  apiSecurityKey?: string;
}

interface ValidateSessionAccessRequest {
  sessionUId: string;
  participantRole: ParticipantRole;
  userUId: string;
}

interface GenerateTokenRequest {
  sessionUId: string;
  participantRole: ParticipantRole;
  userUId: string;
}

interface JoinSessionRequest {
  sessionUId: string;
  participantRole: ParticipantRole;
}

interface SessionUIdRequest {
  sessionUId: string;
}

// Live Interview Service
export const liveInterviewService = {
  /**
   * Get session details by UID
   */
  getSessionByUId: async (
    request: GetSessionRequest
  ): Promise<ApiResponse<SessionDetails>> => {
    return api.interviewAgent.post<ApiResponse<SessionDetails>>(
      API_ENDPOINTS.liveInterview.getSessionByUId,
      request
    );
  },

  /**
   * Validate if a user has access to a session
   */
  validateSessionAccess: async (
    request: ValidateSessionAccessRequest
  ): Promise<ApiResponse<ValidateAccessResponse>> => {
    return api.interviewAgent.post<ApiResponse<ValidateAccessResponse>>(
      API_ENDPOINTS.liveInterview.validateSessionAccess,
      request
    );
  },

  /**
   * Generate ACS token for a participant
   */
  generateToken: async (
    request: GenerateTokenRequest
  ): Promise<ApiResponse<GenerateTokenResponse>> => {
    return api.interviewAgent.post<ApiResponse<GenerateTokenResponse>>(
      API_ENDPOINTS.liveInterview.generateToken,
      request
    );
  },

  /**
   * Join a session (marks participant as joined)
   */
  joinSession: async (
    request: JoinSessionRequest
  ): Promise<ApiResponse<SessionDetails>> => {
    return api.interviewAgent.post<ApiResponse<SessionDetails>>(
      API_ENDPOINTS.liveInterview.joinSession,
      request
    );
  },

  /**
   * Start a session (marks session as InProgress)
   */
  startSession: async (
    request: SessionUIdRequest
  ): Promise<ApiResponse<SessionDetails>> => {
    return api.interviewAgent.post<ApiResponse<SessionDetails>>(
      API_ENDPOINTS.liveInterview.startSession,
      request
    );
  },

  /**
   * End a session (marks session as Completed)
   */
  endSession: async (
    request: SessionUIdRequest
  ): Promise<ApiResponse<SessionDetails>> => {
    return api.interviewAgent.post<ApiResponse<SessionDetails>>(
      API_ENDPOINTS.liveInterview.endSession,
      request
    );
  },
};
